import React, { useCallback, useEffect, useMemo, useState } from 'react';
import PestControlIcon from '@mui/icons-material/PestControl';
import PrintIcon from '@mui/icons-material/Print';
import ModuleHeader from './components/ModuleHeader';
import { useAuth } from './AuthContext';
import {
  ACENTO, COR, Equipe, Opcoes, Programacao, ResumoProgramacao, S, SUB, botaoCor, caixa, gravarSessao, lerSessao, liraaApi,
  mensagemErro, periodo, useCiclosLiraa,
} from './liraaApi';
import { AreaFora, BOTAO_TEAL, C, CSS_LIRAA, CartaoArea, ComoEsta } from './LiraaAreaCard';
import LiraaResumoAba from './LiraaResumoAba';
import BotaoArquivar from './BotaoArquivar';

// Cartao 1 do LIRAa: ciclo, programacao por area (sorteio, programados, inspecionados) e equipe.
// Sessao 30, bloco N3 (24/09/2026 - Walber). So chama /api/liraa (liraaApi.ts).
// Bloco N10: layout em 2 abas (desenho aprovado). Aba 1 "Areas e equipe" (LiraaAreaCard.tsx: um cartao por
// area, so um aberto por vez) e Aba 2 "Resumo e PDF" (LiraaResumoAba.tsx). Regras e contas ficam no motor:
// a tela so le programacao, equipe e resumo-programacao.

const CHAVE_ABA = 'tovis-liraa-ciclo-aba';
const CHAVE_AREA = 'tovis-liraa-ciclo-area';
const SEM_EQUIPE: Equipe[] = [];   // lista vazia estavel (evita refazer o rascunho do cartao a cada render)

export default function LiraaCiclo({ onVoltar, onNavegar }: { onVoltar: () => void; onNavegar: (pagina: string) => void }) {
  const { token } = useAuth();
  const { ciclos, ciclo, cicloId, setCicloId, recarregar, erro: erroCiclos } = useCiclosLiraa(token);

  const [opcoes, setOpcoes] = useState<Opcoes | null>(null);
  const [prog, setProg] = useState<Programacao[]>([]);
  const [equipe, setEquipe] = useState<Equipe[]>([]);
  const [resumo, setResumo] = useState<ResumoProgramacao | null>(null);
  // Arquivos: dados da Programação para o botão "Arquivar" (sem ciclo_id: o ciclo do LIRAa é de outra tabela)
  const pedidoProgramacao = useMemo(() => (ciclo ? {
    pasta: 'liraa' as const, tipo: 'liraa_programacao', chave: `liraa_programacao|lc${ciclo.id}`,
    titulo: `LIRAa · Programação · ${ciclo.nome}`, referencia: `LIRAa ${ciclo.nome}`, avisos: [] as string[],
  } : null), [ciclo]);
  const [erro, setErro] = useState('');
  const [ok, setOk] = useState('');
  const [salvando, setSalvando] = useState<string | null>(null);
  const [gerandoPdf, setGerandoPdf] = useState(false);

  // formulario do ciclo
  const [formCiclo, setFormCiclo] = useState<null | 'novo' | 'editar'>(null);
  const [fNome, setFNome] = useState('');
  const [fIni, setFIni] = useState('');
  const [fFim, setFFim] = useState('');

  // abas, area aberta e alteracao nao salva
  const [aba, setAbaState] = useState<1 | 2>(() => (lerSessao(CHAVE_ABA) === '2' ? 2 : 1));
  const [areaAberta, setAreaAbertaState] = useState<number | null>(() => {
    const v = Number(lerSessao(CHAVE_AREA));
    return Number.isFinite(v) && v > 0 ? v : null;
  });
  const [sujo, setSujo] = useState<number | null>(null);   // area com alteracao nao salva
  const setAba = (a: 1 | 2) => { setAbaState(a); gravarSessao(CHAVE_ABA, String(a)); };
  const setAreaAberta = (id: number | null) => { setAreaAbertaState(id); gravarSessao(CHAVE_AREA, id ? String(id) : null); };

  useEffect(() => {
    liraaApi.opcoes(token).then(setOpcoes).catch(e => setErro(mensagemErro(e)));
  }, [token]);

  const carregar = useCallback(async () => {
    if (!cicloId) { setProg([]); setEquipe([]); setResumo(null); return; }
    try {
      const [p, e, rs] = await Promise.all([
        liraaApi.programacao(token, cicloId), liraaApi.equipe(token, cicloId), liraaApi.resumoProgramacao(token, cicloId),
      ]);
      setProg(p); setEquipe(e); setResumo(rs); setErro('');
    } catch (e) {
      setErro(mensagemErro(e, 'Não foi possível carregar o ciclo.'));
    }
  }, [token, cicloId]);

  useEffect(() => { carregar(); }, [carregar]);
  const recarregarTudo = useCallback(async () => { await carregar(); await recarregar(); }, [carregar, recarregar]);

  const aviso = (msg: string) => { setOk(msg); setTimeout(() => setOk(''), 3000); };

  const nomeArea = (id: number) => prog.find(p => p.area_id === id)?.area_nome
    || opcoes?.areas.find(a => a.id === id)?.nome || 'esta área';
  /** true = pode sair (nada pendente, ou o Walber confirmou perder o que digitou). */
  const podeSair = () => {
    if (sujo === null) return true;
    if (!window.confirm(`Você tem alterações não salvas em ${nomeArea(sujo)}. Sair sem salvar?`)) return false;
    setSujo(null);
    return true;
  };
  const onSujo = useCallback((areaId: number, s: boolean) => {
    setSujo(atual => (s ? areaId : atual === areaId ? null : atual));
  }, []);
  const alternarArea = (id: number) => {
    if (!podeSair()) return;
    setAreaAberta(areaAberta === id ? null : id);
  };
  const trocarAba = (a: 1 | 2) => { if (a !== aba && podeSair()) setAba(a); };
  const trocarCiclo = (id: number | null) => { if (podeSair()) setCicloId(id); };
  const resolver = (areaId: number) => {
    if (!podeSair()) return;
    setAba(1); setAreaAberta(areaId);
    setTimeout(() => document.getElementById(`liraa-area-${areaId}`)?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 80);
  };

  // ── ciclo ──
  const abrirForm = (modo: 'novo' | 'editar') => {
    setErro('');
    setFormCiclo(modo);
    if (modo === 'editar' && ciclo) { setFNome(ciclo.nome); setFIni(ciclo.data_inicio); setFFim(ciclo.data_fim || ''); }
    else { setFNome(''); setFIni(''); setFFim(''); }
  };
  const salvarCiclo = async () => {
    setSalvando('ciclo'); setErro('');
    try {
      const corpo = { nome: fNome, data_inicio: fIni || undefined, data_fim: fFim || null };
      const tinhaFim = formCiclo === 'editar' && !!ciclo?.data_fim;
      const c = formCiclo === 'editar' && ciclo
        ? await liraaApi.editarCiclo(token, ciclo.id, corpo)
        : await liraaApi.criarCiclo(token, corpo);
      setFormCiclo(null);
      await recarregar(c.id);
      if (c.data_fim && !tinhaFim) {
        setOk('Data final salva. O Boletim de Ponto já mostra LIRAa no período para a equipe. Confira e ajuste no menu antes de fechar o ponto.');
        setTimeout(() => setOk(''), 9000);
      } else aviso(`Ciclo "${c.nome}" salvo.`);
    } catch (e) {
      setErro(mensagemErro(e));
    } finally { setSalvando(null); }
  };
  const excluirCiclo = async () => {
    if (!ciclo || !window.confirm(`Excluir o ciclo "${ciclo.nome}"? Só é possível se ele estiver vazio.`)) return;
    setErro('');
    try {
      await liraaApi.excluirCiclo(token, ciclo.id);
      setCicloId(null);
      await recarregar();
      aviso('Ciclo excluído.');
    } catch (e) { setErro(mensagemErro(e)); }
  };

  // ── PDF da programacao (arquivo baixado; nao usa window.print) ──
  const imprimirPdf = async () => {
    if (!cicloId) return;
    setGerandoPdf(true); setErro('');
    try {
      const r = await liraaApi.resumoProgramacao(token, cicloId);
      setResumo(r);
      // carregado so no clique: o jsPDF (~400 KB) nao pesa na abertura do TOVIS
      const { gerarPdfProgramacao, nomeArquivoProgramacao } = await import('./liraaPdf');
      gerarPdfProgramacao(r).save(nomeArquivoProgramacao(r.ciclo.nome));
    } catch (e) {
      setErro(mensagemErro(e, 'Não foi possível gerar o PDF da programação.'));
    } finally { setGerandoPdf(false); }
  };

  // ── areas: no LIRAa (programacao ou equipe = por_area do resumo) e fora ──
  const equipePorArea = useMemo(() => {
    const m = new Map<number, Equipe[]>();
    equipe.forEach(e => m.set(e.area_id, [...(m.get(e.area_id) || []), e]));
    return m;
  }, [equipe]);
  const progPorArea = useMemo(() => new Map(prog.map(p => [p.area_id, p])), [prog]);
  const noLiraa = new Set((resumo?.por_area || []).map(a => a.area_id));
  const fora = prog.filter(p => !noLiraa.has(p.area_id));

  const cartao = (areaId: number, nome: string) => (
    <CartaoArea key={areaId} token={token} cicloId={cicloId!} areaId={areaId} nome={nome}
      prog={progPorArea.get(areaId)?.id ? progPorArea.get(areaId) : undefined}
      equipe={equipePorArea.get(areaId) || SEM_EQUIPE} resumoArea={resumo?.por_area.find(a => a.area_id === areaId)}
      agentes={opcoes?.agentes || []} aberto={areaAberta === areaId} onAlternar={() => alternarArea(areaId)}
      onSujo={onSujo} onRecarregar={recarregarTudo} />
  );

  const semCiclo = ciclos !== null && ciclos.length === 0;
  const estiloAba = (ativa: boolean): React.CSSProperties => ({
    minHeight: 40, padding: '8px 18px', borderRadius: 9, border: 'none', cursor: 'pointer', fontSize: 15, fontWeight: 800,
    fontFamily: 'inherit', background: ativa ? '#ffffff' : 'transparent', color: ativa ? C.texto : C.sub,
    boxShadow: ativa ? '0 1px 3px rgba(15,30,54,.18)' : 'none',
  });

  return (
    <div style={{ ...S.pagina, background: C.fundo, color: C.texto }}>
      <style>{CSS_LIRAA}</style>
      <ModuleHeader
        titulo="LIRAa — Ciclo e Equipe"
        subtitulo="Ciclo, áreas sorteadas, equipe e imóveis inspecionados"
        icone={<PestControlIcon />}
        accentColor={ACENTO.ciclo}
        onVoltar={onVoltar}
        chips={[
          { label: 'Ciclo', value: ciclo?.nome || '—' },
          { label: 'Período', value: periodo(ciclo) },
          { label: 'Tubitos', value: ciclo ? `${ciclo.tubitos} (${ciclo.pendentes} pendentes)` : '—' },
        ]}
      />
      <div style={S.miolo}>
        {/* topo: ciclo */}
        <div style={{ ...S.card, display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'flex-end' }}>
          <label style={{ flex: '1 1 240px' }}><span style={S.rotulo}>🔁 Ciclo LIRAa</span>
            <select style={S.campo} value={cicloId ?? ''} onChange={e => trocarCiclo(Number(e.target.value) || null)} disabled={!ciclos?.length}>
              {!ciclos?.length && <option value="">— nenhum ciclo —</option>}
              {ciclos?.map(c => <option key={c.id} value={c.id}>{c.nome}{c.em_andamento ? ' (em andamento)' : ''}</option>)}
            </select>
          </label>
          <button style={botaoCor(ACENTO.ciclo)} onClick={() => abrirForm('novo')}>＋ Novo ciclo</button>
          <button style={S.botao} onClick={() => abrirForm('editar')} disabled={!ciclo}>✎ Editar ciclo</button>
          <button style={{ ...S.botao, borderColor: COR.vermelho.forte, color: COR.vermelho.texto }} onClick={excluirCiclo} disabled={!ciclo}>🗑 Excluir ciclo</button>
        </div>

        {erroCiclos && <div style={caixa('vermelho')}>{erroCiclos}</div>}
        {erro && <div style={caixa('vermelho')}>{erro}</div>}
        {ok && <div style={caixa('verde')}>✓ {ok}</div>}

        {formCiclo && (
          <div style={{ ...S.card, borderLeft: `6px solid ${ACENTO.ciclo}` }}>
            <h3 style={S.tituloSecao}>{formCiclo === 'novo' ? 'Novo ciclo' : `Editar ciclo "${ciclo?.nome}"`}</h3>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 12 }}>
              <label><span style={S.rotulo}>Nome</span>
                <input style={S.campo} value={fNome} maxLength={40} onChange={e => setFNome(e.target.value)} placeholder="ex.: LIRAa 3/2026" /></label>
              <label><span style={S.rotulo}>Início</span>
                <input style={S.campo} type="date" value={fIni} onChange={e => setFIni(e.target.value)} /></label>
              <label><span style={S.rotulo}>Fim (opcional)</span>
                <input style={S.campo} type="date" value={fFim} onChange={e => setFFim(e.target.value)} />
                <span style={{ fontSize: 12, color: SUB }}>Informe a data final quando o ciclo terminar. Ao salvar, o Boletim de Ponto preenche LIRAa no período para a equipe (dias úteis sem boletim e sem marcação).</span></label>
            </div>
            <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
              <button style={botaoCor(ACENTO.ciclo)} onClick={salvarCiclo} disabled={salvando === 'ciclo'}>{salvando === 'ciclo' ? 'Salvando…' : 'Salvar ciclo'}</button>
              <button style={S.botao} onClick={() => setFormCiclo(null)}>Cancelar</button>
            </div>
          </div>
        )}

        {semCiclo && !formCiclo && (
          <div style={caixa('cinza')}>Nenhum ciclo ainda. Clique em <b>＋ Novo ciclo</b> para começar.</div>
        )}

        {ciclo && (
          <>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 14 }}>
              <button style={botaoCor(ACENTO.laboratorio)} onClick={() => onNavegar('liraa_laboratorio')}>🧪 Abrir Laboratório LIRAa</button>
              <button style={botaoCor(ACENTO.quadro)} onClick={() => onNavegar('liraa_quadro')}>📊 Abrir Quadro de Positividade LIRAa</button>
            </div>

            {/* A — abas + PDF */}
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'center', marginBottom: 14 }}>
              <div role="tablist" style={{ display: 'inline-flex', gap: 4, background: C.abas, borderRadius: 11, padding: 4 }}>
                <button role="tab" aria-selected={aba === 1} style={estiloAba(aba === 1)} onClick={() => trocarAba(1)}>1 · Áreas e equipe</button>
                <button role="tab" aria-selected={aba === 2} style={estiloAba(aba === 2)} onClick={() => trocarAba(2)}>2 · Resumo e PDF</button>
              </div>
              <button className="liraa-teal" style={{ ...BOTAO_TEAL, marginLeft: 'auto' }} onClick={imprimirPdf} disabled={gerandoPdf}>
                <PrintIcon fontSize="small" /> {gerandoPdf ? 'Gerando PDF…' : 'Imprimir programação (PDF)'}</button>
              <BotaoArquivar pedido={pedidoProgramacao} gerar={async () => {
                if (!cicloId) throw new Error('Escolha o ciclo do LIRAa.');
                const r = await liraaApi.resumoProgramacao(token, cicloId);
                const { gerarPdfProgramacao, nomeArquivoProgramacao } = await import('./liraaPdf');
                return { arquivo: gerarPdfProgramacao(r).output('blob'), nome: nomeArquivoProgramacao(r.ciclo.nome) };
              }} />
            </div>

            {!resumo && !erro && <div style={{ color: SUB }}>Carregando…</div>}

            {/* B — Aba 1: areas e equipe */}
            {resumo && aba === 1 && (
              <>
                <ComoEsta resumo={resumo} />
                {resumo.por_area.length === 0 && (
                  <div style={caixa('cinza')}>Nenhuma área no LIRAa ainda. Use <b>Incluir no ciclo</b> numa área abaixo.</div>
                )}
                {resumo.por_area.map(a => cartao(a.area_id, a.area))}
                {fora.length > 0 && <div style={{ fontWeight: 800, color: C.sub, margin: '16px 0 8px' }}>Áreas fora deste ciclo</div>}
                {fora.map(p => (areaAberta === p.area_id
                  ? cartao(p.area_id, p.area_nome)
                  : <AreaFora key={p.area_id} nome={p.area_nome} onIncluir={() => alternarArea(p.area_id)} />))}
              </>
            )}

            {/* C — Aba 2: resumo e PDF */}
            {resumo && aba === 2 && <LiraaResumoAba resumo={resumo} onResolver={resolver} />}
          </>
        )}
      </div>
    </div>
  );
}
